/**
 * Reconciles a pinned Table Explorer view's persisted column references
 * (filters, sorting, selected columns) against the live table's columns.
 *
 * A pin captured against an earlier schema can carry filters/sorts/selected
 * columns that no longer exist. Running the pinned view's data query with those
 * stale references would emit broken SQL, so the open-from-pin flow gates the
 * data query behind `pendingColumnReconcile` and calls this pure function once
 * live column metadata has arrived.
 *
 * Rules:
 *  - Filter groups whose `field` vanished are dropped entirely.
 *  - Sort definitions on vanished columns are dropped.
 *  - An explicit column selection is pruned of vanished names.
 *  - `'*'` (all columns) passes through untouched.
 *  - If EVERY selected column vanished, fall back to `'*'` — a Table Explorer
 *    view must never emit an empty projection (G8).
 *  - `droppedCount` counts each removed reference (one per dropped filter group,
 *    one per dropped sort, one per pruned column) so the caller can surface an
 *    honest "N saved settings … were removed" toast.
 *
 * Column matching (K5):
 *  - An EXACT (byte-for-byte) match keeps the reference untouched.
 *  - Otherwise a normalized match (via `normalizeColumnName`, the same helper
 *    the rest of Table Explorer uses) REWRITES the kept reference to the live
 *    column's exact spelling. Keeping the pinned spelling would emit a quoted
 *    identifier that fails on case-sensitive engines (PG `"Status"` vs live
 *    `status`).
 *  - If a normalized key matches MULTIPLE live columns and none matches
 *    exactly, the reference is ambiguous — there is no safe rewrite — so it is
 *    dropped and counted.
 * Rewrites are NOT drops: `droppedCount` counts only removed references.
 */
import type { FilterGroup, SortDefinition } from '../../core/types/workspaceTypes';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';

export type ReconcilePinnedColumnsInput = {
  filters: FilterGroup[];
  sorting: SortDefinition[];
  selectedColumns: string[] | '*';
};

export type ReconcilePinnedColumnsResult = {
  filters: FilterGroup[];
  sorting: SortDefinition[];
  selectedColumns: string[] | '*';
  droppedCount: number;
};

/** How a pinned column reference resolves against the live schema. */
type ColumnResolution =
  | { kind: 'keep' } // exact match — reference unchanged
  | { kind: 'rewrite'; name: string } // normalized match — adopt the live spelling
  | { kind: 'drop' }; // vanished, or ambiguous with no exact hit

function buildColumnResolver(liveColumns: string[]): (field: string) => ColumnResolution {
  const exact = new Set(liveColumns);
  const byNormalized = new Map<string, string[]>();
  for (const col of liveColumns) {
    const key = normalizeColumnName(col);
    const existing = byNormalized.get(key);
    if (existing) existing.push(col);
    else byNormalized.set(key, [col]);
  }

  return (field: string): ColumnResolution => {
    if (exact.has(field)) return { kind: 'keep' };
    const candidates = byNormalized.get(normalizeColumnName(field)) ?? [];
    // 0 candidates → vanished; >1 → ambiguous (no exact match to disambiguate).
    if (candidates.length === 1) return { kind: 'rewrite', name: candidates[0] };
    return { kind: 'drop' };
  };
}

export function reconcilePinnedColumns(
  content: ReconcilePinnedColumnsInput,
  liveColumns: string[],
): ReconcilePinnedColumnsResult {
  const resolve = buildColumnResolver(liveColumns);

  let droppedCount = 0;

  const filters: FilterGroup[] = [];
  for (const group of content.filters) {
    const r = resolve(group.field);
    if (r.kind === 'drop') {
      droppedCount += 1;
      continue;
    }
    filters.push(r.kind === 'rewrite' ? { ...group, field: r.name } : group);
  }

  const sorting: SortDefinition[] = [];
  for (const sort of content.sorting) {
    const r = resolve(sort.field);
    if (r.kind === 'drop') {
      droppedCount += 1;
      continue;
    }
    sorting.push(r.kind === 'rewrite' ? { ...sort, field: r.name } : sort);
  }

  let selectedColumns: string[] | '*';
  if (content.selectedColumns === '*') {
    selectedColumns = '*';
  } else {
    const kept: string[] = [];
    for (const name of content.selectedColumns) {
      const r = resolve(name);
      if (r.kind === 'drop') {
        droppedCount += 1;
        continue;
      }
      kept.push(r.kind === 'rewrite' ? r.name : name);
    }
    // Never emit an empty projection — fall back to all columns (G8).
    selectedColumns = kept.length > 0 ? kept : '*';
  }

  return { filters, sorting, selectedColumns, droppedCount };
}

/**
 * Shape of a settled TanStack metadata refetch, narrowed to what the reconcile
 * gate needs (structurally compatible with `QueryObserverResult<TableMetadata>`).
 */
export type MetadataRefetchResult = {
  isError: boolean;
  data?: { columns: ReadonlyArray<{ name: string }> } | undefined;
};

/**
 * Columns from a FORCED metadata refetch, for the open-from-pin reconcile gate
 * (L2). The gate must reconcile ONLY against a successful fresh fetch:
 *  - refetch errored → `null`. TanStack keeps the previous (possibly stale)
 *    cache entry in `result.data` on error — returning those columns would
 *    reconcile against exactly the stale data the forced refetch exists to
 *    avoid, so the caller must take the terminal-error path instead.
 *  - refetch succeeded → the fresh column names; `[]` when the fetch settled
 *    without metadata (dropped table, or the queryFn's IPC-level failure which
 *    resolves `undefined`) — the caller's terminal no-columns path.
 */
export function freshColumnsFromMetadataRefetch(result: MetadataRefetchResult): string[] | null {
  if (result.isError) return null;
  return (result.data?.columns ?? []).map((c) => c.name);
}
